import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';
import { useGenerateKidDocument } from '../../../../../../api/kids/hooks/mutations';
import type { KidDocumentForm, PdfElement } from '../../types/KidPdfGeneratorV2.types';
import { createMinimalBlankPdfUrl } from '../../../kids/utils/pdf.utils';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../../../redux/auth/selectors';
import {
  createSectionByIdSelector,
  createPageDetailsSelector,
} from '../../../../../../redux/pages/selectors';
import { raptorPlatformIdSelector } from '../../../../../../redux/raptorPlatform/selectors';
import { createResourceCacheKey } from '../../../../../layout/general/SimpleGeneralPageWrapper';
import { useFunds } from '../../../../../../api/funds';
import TokenService from '../../../../../../services/token-service';
import {
  useGetAllKidTemplates,
  useGetKidData,
} from '../../../../../../api/kids/hooks/queries';
import { transformKidsDataToPdfElement } from '../../utils/kidDataTransformers';
import {
  buildTemplatePdfElements,
  getSelectedTemplates,
} from './templateSelection.utils';
import DocumentPdfPreview from './DocumentPdfPreview';


// Centralised PDF generation logic based on creation method.
// - Duplicate: static example PDF
// - New + blank: minimal local blank PDF
// - New + recommended: generate via backend

interface DocumentPreviewProps {
  documentSourceType: CreateKidDocumentValues['documentSourceType'];
  creationMethod: CreateKidDocumentValues['creationMethod'];
  shareClassName: string;
  shareClassId: string;
}

const DocumentPreview: FunctionComponent<DocumentPreviewProps> = ({
  documentSourceType,
  creationMethod,
  shareClassName,
  shareClassId,
}) => {
  const { watch } = useFormContext<CreateKidDocumentValues>();
  const fundId = watch('fundId');
  const fundName = watch('fundName');
  const language = watch('language');
  const layoutElements = watch('layoutElements') ?? [];
  const templateShareClassId = watch('template_share_class_id');
  const templateFundId = watch('template_fund_id');
  const templateShareClassName = watch('template_share_class_name');
  const selectedTemplateIds = watch('selectedTemplateIds') ?? [];
  const clientName = useSelector(clientNameSelector);
  const { user_client_id: clientId } = TokenService.getUser()!;

  // Load funds via API so we can resolve templateFundId -> fund model
  const sectionDetails = useSelector(createSectionByIdSelector('raidr-v2'));
  const pageDetails = useSelector(
    createPageDetailsSelector('kid-overview-v2', 'raidr-v2'),
  );
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );
  const { data: fundsData } = useFunds({
    fundTypes: sectionDetails?.fundTypes,
    rsResources,
    raptorPlatformId,
  });


  const { data: duplicateKidData } = useGetKidData(
    clientId,
    templateFundId || '',
    templateShareClassId || '',
    language,
  );

  const { data: allTemplates } = useGetAllKidTemplates(clientId, undefined, true, {
    enabled: !!clientId,
  });
  const selectedTemplates = useMemo(
    () =>
      getSelectedTemplates(
        selectedTemplateIds,
        (allTemplates || []).filter(
          (template) => !language || template.document_language === language,
        ),
      ),
    [allTemplates, language, selectedTemplateIds],
  );

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const generateKidDocumentMutation = useGenerateKidDocument();
  const hasRecommendedLayoutSelection =
    creationMethod === 'recommended' && layoutElements.length > 0;
  const shouldShowNoSourceSelectionMessage = !documentSourceType;
  const shouldShowNoMethodSelectionMessage =
    documentSourceType === 'new' && !creationMethod;
  const shouldShowNoSelectionMessage =
    documentSourceType === 'new' &&
    creationMethod === 'recommended' &&
    !hasRecommendedLayoutSelection;
  const shouldShowNoDuplicateSelectionMessage =
    (documentSourceType === 'duplicate' || creationMethod === 'duplicate') &&
    !templateShareClassId;

  useEffect(() => {
    let isCancelled = false;

    const generatePdf = async () => {
      // Reset mutation state when inputs change
      generateKidDocumentMutation.reset();

      // Duplicate flow: generate preview from existing KID data
      if (documentSourceType === 'duplicate' || creationMethod === 'duplicate') {
        if (!templateShareClassId || !templateFundId || !language) {
          setPreviewUrl(null);
          return;
        }

        if (!duplicateKidData || duplicateKidData.length === 0) {
          setPreviewUrl(null);
          return;
        }

        try {
          const templateFundName = fundsData?.find(
            (f) => f.fund_id === templateFundId,
          );
          const baseElements = duplicateKidData.map((element, index) =>
            transformKidsDataToPdfElement(element as any, index),
          );
          const templateElements = buildTemplatePdfElements(
            selectedTemplates,
            baseElements
              .map((element) => element.template_id)
              .filter((templateId): templateId is string => Boolean(templateId)),
          );

          const payload: KidDocumentForm = {
            client: clientName || '',
            fund_name: templateFundName?.fund_name || '',
            fund_name_full: templateFundName?.fund_name_full || '',
            share_class: templateShareClassName || shareClassName,
            date:
              duplicateKidData[0]?.kid_id_create_date ??
              new Date().toISOString(),
            elements: [...baseElements, ...templateElements],
          };

          const response = await generateKidDocumentMutation.mutateAsync(
            payload,
          );
          if (isCancelled) return;
          const blob = new Blob([response.data], {
            type: 'application/pdf',
          });
          if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
          const url = URL.createObjectURL(blob);
          objectUrlRef.current = url;
          setPreviewUrl(url);
        } catch (err) {
          if (isCancelled) return;
          // eslint-disable-next-line no-console
          console.error('Failed to generate duplicate preview PDF', err);
          setPreviewUrl(null);
        }
        return;
      }

      // For now we only generate previews for "new" documents
      if (documentSourceType !== 'new') {
        setPreviewUrl(null);
        return;
      }

      // Blank documents: generate a minimal blank PDF locally (no API call)
      if (creationMethod === 'blank') {
        if (selectedTemplates.length > 0) {
          try {
            const payload: KidDocumentForm = {
              client: clientName || '',
              fund_name: fundName,
              fund_name_full: fundName,
              share_class: shareClassName,
              date: new Date().toISOString(),
              elements: buildTemplatePdfElements(selectedTemplates),
            };
            const response = await generateKidDocumentMutation.mutateAsync(payload);
            if (isCancelled) return;
            const blob = new Blob([response.data], { type: 'application/pdf' });
            if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
            const url = URL.createObjectURL(blob);
            objectUrlRef.current = url;
            setPreviewUrl(url);
          } catch (err) {
            if (isCancelled) return;
            // eslint-disable-next-line no-console
            console.error('Failed to generate template preview PDF', err);
            setPreviewUrl(null);
          }
          return;
        }

        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const blankUrl = createMinimalBlankPdfUrl();
        if (isCancelled) return;
        objectUrlRef.current = blankUrl;
        setPreviewUrl(blankUrl);
        return;
      }

      // Recommended/layout documents: generate via backend
      if (creationMethod !== 'recommended') {
        setPreviewUrl(null);
        return;
      }

      // If no layout elements are available yet, don't call backend.
      if (!layoutElements || layoutElements.length === 0) {
        setPreviewUrl(null);
        return;
      }

      try {
        const elements: PdfElement[] = [];
        layoutElements.forEach((kidsData, index) => {
          elements.push(transformKidsDataToPdfElement(kidsData, index));
        });
        const templateElements = buildTemplatePdfElements(
          selectedTemplates,
          elements
            .map((element) => element.template_id)
            .filter((templateId): templateId is string => Boolean(templateId)),
        );

        const templateFundName = fundsData?.find((f) => f.fund_id === templateFundId)?.fund_name || '';

        const payload: KidDocumentForm = {
          client: clientName || '',
          fund_name: templateFundName,
          fund_name_full: fundName,
          share_class: shareClassName,
          date: new Date().toISOString(),
          elements: [...elements, ...templateElements],
        };
        const response = await generateKidDocumentMutation.mutateAsync(payload);
        if (isCancelled) return;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        if (isCancelled) return;
        // eslint-disable-next-line no-console
        console.error('Failed to generate preview PDF', err);
      }
    };

    generatePdf();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentSourceType,
    creationMethod,
    fundId,
    fundName,
    shareClassName,
    layoutElements,
    templateShareClassId,
    templateFundId,
    templateShareClassName,
    language,
    selectedTemplates,
    duplicateKidData,
  ]);

  // Cleanup object URL when component unmounts
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);


  const emptyMessage = shouldShowNoSourceSelectionMessage
    ? 'No selection has been made yet. Choose a document type to see a preview.'
    : shouldShowNoMethodSelectionMessage
      ? 'No selection has been made yet. Choose blank document or recommended layout to see a preview.'
      : shouldShowNoSelectionMessage
        ? 'No selection has been made yet. Choose a document layout option to see a preview.'
        : shouldShowNoDuplicateSelectionMessage
          ? 'No selection has been made yet. Select a fund and share class to see a preview.'
          : undefined;

  return (
    <DocumentPdfPreview
      previewUrl={previewUrl}
      isPending={generateKidDocumentMutation.isPending}
      isError={generateKidDocumentMutation.isError}
      title="Document Preview"
      emptyMessage={emptyMessage}
      loadingMessage="Generating preview…"
      errorMessage="Failed to load document preview."
    />
  );
};

export default DocumentPreview;

